import * as React from 'react';
import { FC } from 'react';
import { Label } from 'reactstrap';
import { ReportFieldDefinition } from '../../../../models';
import { Marker, parseMarkers } from '../../../../shared/marker';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import ButtonsContainer from '../../../ButtonsContainer';
import { FormField, MultipleCheckboxes } from '../../fields';
import { Option } from '../../fields/MultipleCheckboxes';
import AppButton from '../../../AppButton';

export interface ImageMarkerFieldProps {
    id: string,
    fieldDefinition: ReportFieldDefinition,
    value?: string,
    style?: React.CSSProperties,
    onChange?: (value: string) => void,
    readOnly?: boolean,
}

const calculateCoordinatesPercentage = (refElem: React.MutableRefObject<HTMLElement|undefined>, relX: number, relY: number): { x: number, y: number } => {
    if(!refElem.current) throw Error('Ref current must have value');
    const refWidth = refElem.current.offsetWidth;
    const refHeight = refElem.current.offsetHeight;
    return { x: relX / refWidth, y: relY / refHeight };
}

const getRelativeX = (refElem: React.MutableRefObject<HTMLElement|undefined>, x: number): number => {
    if(!refElem.current) throw Error('Ref current must have value');
    const refWidth = refElem.current.offsetWidth;
    return x*refWidth;
}

const getRelativeY = (refElem: React.MutableRefObject<HTMLElement|undefined>, y: number): number => {
    if(!refElem.current) throw Error('Ref current must have value');
    const refHeight = refElem.current.offsetHeight;
    return y*refHeight;
}

const marksSeparator = '|';
const fieldsSeparator = ';';

interface Coordinate {
    x: number,
    y: number,
}

interface Mark extends Coordinate {
    markerIndex: number,
}

const parseMarks = (str: string): Mark[] => str
    .split(marksSeparator)
    .filter(v => !!v)
    .map(value => value.split(fieldsSeparator))
    .filter(value => value.length == 3)
    .map(value => ({x: parseFloat(value[0]), y: parseFloat(value[1]), markerIndex: parseInt(value[2])}));

const marksToString = (values: Mark[]): string => 
    values.map(({x, y, markerIndex}) => `${x}${fieldsSeparator}${y}${fieldsSeparator}${markerIndex}`).join(marksSeparator);

const ImageMarkerField : FC<ImageMarkerFieldProps> = ({
    id,
    fieldDefinition,
    style,
    value,
    onChange,
    readOnly,
}) => {

    const [selectedMarkerIndex, setSelectedMarkerIndex] = React.useState<number|null>(null);

    const t = useTranslations();

    const imgRef = React.useRef<HTMLImageElement>()

    const markers = React.useMemo<Marker[]>(() => parseMarkers(fieldDefinition.aditionalFields?.markers ?? ''), [fieldDefinition]);
    const marks = React.useMemo<Mark[]>(() => value ? parseMarks(value) : [], [value]);

    const options = React.useMemo<Option[]>(
        () => markers.map(m => ({ value: m.name, element: (<Label style={{color: m.color}}>{m.symbol} {m.name}</Label>)})), 
        [markers])

    const setMarks = React.useCallback((values: Mark[]) => !readOnly && onChange && onChange(marksToString(values)), [onChange, readOnly]);

    const onMouseUp = React.useCallback<React.MouseEventHandler<HTMLElement>>(event => {
        if (selectedMarkerIndex === null) {
            const markerCoordinatesToDelete = (event.target as HTMLElement)?.parentElement?.id?.split(',') ?? [];
            if (markerCoordinatesToDelete.length === 2) {
                const x = parseFloat(markerCoordinatesToDelete[0]).toFixed(3);
                const y = parseFloat(markerCoordinatesToDelete[1]).toFixed(3);
                setMarks(marks.filter(mark => !(mark.x.toFixed(3) === x && mark.y.toFixed(3) === y)));
            }
        } else {
            const marker = markers[selectedMarkerIndex];
            const rect = imgRef.current!.getBoundingClientRect();
            const relX = event.clientX - rect.left - marker.size/2;
            const relY = event.clientY - rect.top - marker.size*3/4;
            const {x, y} = calculateCoordinatesPercentage(imgRef, relX, relY);
            setMarks([...marks, {x, y, markerIndex: selectedMarkerIndex}]);
        }
    }, [imgRef, marks, setMarks, selectedMarkerIndex]);

    const onSelectMarkerChange = React.useCallback((selectedValues: string[]) => {
        const selectedIndex = selectedValues.length ? markers.findIndex(marker => marker.name === selectedValues[0]) : null;
        setSelectedMarkerIndex(selectedIndex);
    }, [markers, setSelectedMarkerIndex]);

    return (
        <div style={{position: 'relative'}}>
            <div id={`${id}-image-div`} onMouseUp={onMouseUp}>
                {imgRef.current && marks.map(mark => {
                    const x = getRelativeX(imgRef, mark.x);
                    const y = getRelativeY(imgRef, mark.y)
                    return (
                        <span key={`${x},${y}`} id={`${mark.x},${mark.y}`} style={{
                            position: 'absolute', 
                            width: 0,
                            height: 0,
                            left: x, 
                            top: y, 
                            transform: 'translate(-50%, -50%)',
                            userSelect: 'none',
                            color: markers[mark.markerIndex]?.color,
                            fontSize: `${markers[mark.markerIndex]?.size ?? 20}px`,
                        }}>
                            <span style={{transform: 'translate(-50%, -50%)',}}>
                            {markers[mark.markerIndex]?.symbol}
                            </span>
                        </span>
                    );
                })}
                <img style={{maxWidth: '100%', ...style}} ref={imgRef as any} src={fieldDefinition.aditionalFields?.image} />
            </div> 
            {options.length > 0 && 
                <FormField>
                    <>
                        <MultipleCheckboxes 
                            id={`${id}-marker`}
                            single
                            disabled={readOnly}
                            values={selectedMarkerIndex === null ? [] : [markers[selectedMarkerIndex].name]}
                            onChange={onSelectMarkerChange}
                            options={options}
                        />
                        { !readOnly && <ButtonsContainer>
                            <AppButton id={`${id}-clear-markers-button`} onClick={() => setMarks([])}>{t(TK.clearMarkers)}</AppButton>
                        </ButtonsContainer> }
                    </>
                </FormField>
            }
        </div>
    );
}

export default ImageMarkerField;
